import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type {
  HostingProviderPage,
  HostingProviderView,
  HostingSupplierCandidate,
  HostingSupplierPage,
} from '@tosho/contracts';
import { BusinessLogo } from './BusinessLogo';
import { PrototypeIcon } from './PrototypeIcon';
import {
  hostingProviderApi,
  hostingProviderError,
  hostingProviderLogoUrl,
  HostingProviderRequestError,
} from './hosting-provider-api';

export function HostingProvidersPanel({
  csrfToken,
  onClose,
}: {
  readonly csrfToken: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const [q, setQ] = useState(''),
    [page, setPage] = useState(0),
    [visit, setVisit] = useState(0);
  const [data, setData] = useState<HostingProviderPage | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState<string | null>(null),
    [feedback, setFeedback] = useState<string | null>(null);
  const [editor, setEditor] = useState<HostingProviderView | 'new' | null>(null);
  const heading = useRef<HTMLHeadingElement>(null),
    add = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void hostingProviderApi
        .list(q, page)
        .then((result) => {
          if (active) setData(result);
        })
        .catch((reason) => {
          if (active) {
            setData(null);
            setError(hostingProviderError(reason));
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [q, page, visit]);
  return (
    <div className="hosting-servers hosting-providers">
      <header className="dashboard-heading hosting-providers__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Réglages</p>
          <h1 ref={heading} tabIndex={-1}>
            Hébergeurs
          </h1>
          <p>
            Les hébergeurs de votre organisation, choisis depuis Freebe ou ajoutés manuellement.
          </p>
        </div>
        <div className="hosting-providers__heading-actions">
          <button type="button" onClick={onClose}>
            <PrototypeIcon name="arrow-left" /> Retour aux serveurs
          </button>
          <button
            type="button"
            ref={add}
            className="hosting-servers__primary"
            onClick={() => setEditor('new')}
          >
            <PrototypeIcon name="plus" /> Ajouter un hébergeur
          </button>
        </div>
      </header>
      {feedback && (
        <p role="status" className="hosting-servers__feedback tosho-feedback is-success">
          {feedback}
        </p>
      )}
      <section className="hosting-servers__card" aria-label="Catalogue des hébergeurs">
        <div className="hosting-servers__toolbar hosting-servers__catalogue-toolbar">
          <div className="hosting-servers__toolbar-title">
            <PrototypeIcon name="database" />
            <div>
              <h2>Hébergeurs du catalogue</h2>
              <p>
                {data
                  ? `${data.total} hébergeur${data.total > 1 ? 's' : ''}`
                  : 'Chargement du catalogue'}
              </p>
            </div>
          </div>
          <button type="button" disabled={loading} onClick={() => setVisit((n) => n + 1)}>
            <PrototypeIcon name="refresh-cw" /> Actualiser
          </button>
        </div>
        <div className="hosting-providers__search">
          <label className="hosting-providers__search-field">
            <span className="sr-only">Rechercher un hébergeur</span>
            <PrototypeIcon name="search" />
            <input
              placeholder="Rechercher un hébergeur…"
              value={q}
              maxLength={120}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
          </label>
          <p>
            <PrototypeIcon name="shield-check" /> Les noms et logos gérés ici sont conservés lors
            des imports Freebe.
          </p>
        </div>
        {loading ? (
          <p role="status" className="hosting-servers__empty">
            Chargement des hébergeurs…
          </p>
        ) : error ? (
          <p role="alert" className="hosting-servers__error tosho-feedback is-error">
            {error}
          </p>
        ) : data?.items.length === 0 ? (
          <p className="hosting-servers__empty">
            Aucun hébergeur dans ces résultats. Ajoutez-en un depuis un fournisseur Freebe ou par
            saisie manuelle.
          </p>
        ) : (
          <table className="hosting-providers__table">
            <caption className="sr-only">Hébergeurs et provenance</caption>
            <thead>
              <tr>
                <th>Hébergeur</th>
                <th>Provenance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((provider) => (
                <tr key={provider.id}>
                  <td data-label="Hébergeur">
                    <BusinessLogo
                      name={provider.displayName}
                      logoUrl={hostingProviderLogoUrl(provider)}
                    />
                  </td>
                  <td data-label="Provenance">
                    <span className="hosting-providers__origin">
                      <PrototypeIcon
                        name={
                          provider.origin === 'FREEBE'
                            ? 'cloud-upload'
                            : provider.origin === 'SERVER_DECLARATION'
                              ? 'database'
                              : 'pencil'
                        }
                      />
                      {origin(provider)}
                    </span>
                    {provider.sourceDisplayName && (
                      <small>Fournisseur : {provider.sourceDisplayName}</small>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="hosting-servers__icon-button"
                      title={'Modifier ' + provider.displayName}
                      aria-label={'Modifier ' + provider.displayName}
                      onClick={() => setEditor(provider)}
                    >
                      <PrototypeIcon name="pencil" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <footer className="hosting-servers__pagination">
          <span>
            Page {page + 1}
            {data ? ' sur ' + Math.max(1, Math.ceil(data.total / data.pageSize)) : ''}
          </span>
          <div>
            <button
              type="button"
              disabled={loading || page === 0}
              onClick={() => setPage((n) => n - 1)}
            >
              Précédente
            </button>
            <button
              type="button"
              disabled={loading || !data || (page + 1) * data.pageSize >= data.total}
              onClick={() => setPage((n) => n + 1)}
            >
              Suivante
            </button>
          </div>
        </footer>
      </section>
      {editor && (
        <ProviderEditor
          provider={editor === 'new' ? null : editor}
          csrfToken={csrfToken}
          onClose={() => {
            setEditor(null);
            if (editor === 'new') add.current?.focus();
          }}
          onChanged={() => {
            setVisit((n) => n + 1);
            setFeedback('La fiche hébergeur est enregistrée.');
          }}
        />
      )}
    </div>
  );
}
function origin(provider: HostingProviderView): string {
  return provider.origin === 'FREEBE'
    ? 'Fournisseur Freebe sélectionné'
    : provider.origin === 'SERVER_DECLARATION'
      ? 'Repris des déclarations serveur'
      : 'Ajout manuel';
}
function ProviderEditor({
  provider,
  csrfToken,
  onClose,
  onChanged,
}: {
  readonly provider: HostingProviderView | null;
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const [current, setCurrent] = useState(provider),
    [name, setName] = useState(provider?.displayName ?? ''),
    [mode, setMode] = useState<'MANUAL' | 'FREEBE'>('MANUAL'),
    [attach, setAttach] = useState(false),
    [source, setSource] = useState<HostingSupplierCandidate | null>(null);
  const [busy, setBusy] = useState(false),
    [conflict, setConflict] = useState(false),
    [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  async function perform(work: () => Promise<HostingProviderView>): Promise<void> {
    if (busy || conflict) return;
    setBusy(true);
    setError(null);
    try {
      const result = await work();
      setCurrent(result);
      setName(result.displayName);
      setAttach(false);
      setSource(null);
      onChanged();
    } catch (reason) {
      setError(hostingProviderError(reason));
      setConflict(
        reason instanceof HostingProviderRequestError &&
          reason.code === 'HOSTING_PROVIDER_CONFLICT',
      );
    } finally {
      setBusy(false);
    }
  }
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!current && mode === 'FREEBE' && !source) {
      setError('Choisissez explicitement un fournisseur Freebe.');
      return;
    }
    void perform(() =>
      current
        ? hostingProviderApi.rename(current, name.trim(), csrfToken)
        : hostingProviderApi.create(name.trim(), mode === 'FREEBE' ? source!.id : null, csrfToken),
    );
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor hosting-providers-editor"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          aria-label="Fermer"
          onClick={onClose}
          disabled={busy}
        />
      }
    >
      <form onSubmit={submit}>
        <header className="tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Catalogue Hébergeurs</p>
            <h2 id={title}>{current ? 'Modifier l’hébergeur' : 'Ajouter un hébergeur'}</h2>
          </div>
        </header>
        {error && (
          <p role="alert" className="hosting-servers__error tosho-feedback is-error">
            {error}
          </p>
        )}
        <fieldset disabled={busy || conflict}>
          {!current && (
            <div className="hosting-providers-editor__modes">
              <button
                type="button"
                aria-pressed={mode === 'MANUAL'}
                onClick={() => {
                  setMode('MANUAL');
                  setSource(null);
                }}
              >
                Saisie manuelle
              </button>
              <button
                type="button"
                aria-pressed={mode === 'FREEBE'}
                onClick={() => {
                  setMode('FREEBE');
                  setSource(null);
                }}
              >
                Depuis Freebe
              </button>
            </div>
          )}
          {!current && mode === 'FREEBE' && (
            <SupplierPicker
              selected={source}
              onSelect={(candidate) => {
                setSource(candidate);
                setName(candidate.displayName.slice(0, 160));
              }}
            />
          )}
          <label>
            Nom de l’hébergeur
            <input
              autoFocus
              required
              maxLength={160}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="hosting-servers__primary"
            disabled={!name.trim() || (!current && mode === 'FREEBE' && !source)}
          >
            {busy ? 'Enregistrement…' : current ? 'Enregistrer le nom' : 'Créer l’hébergeur'}
          </button>
          {current && (
            <>
              <section className="hosting-providers-editor__logo" aria-label="Logo de l’hébergeur">
                <h3>Logo</h3>
                <BusinessLogo
                  name={current.displayName}
                  logoUrl={hostingProviderLogoUrl(current)}
                />
                <label>
                  {current.hasLogo ? 'Remplacer le logo' : 'Ajouter un logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      if (
                        !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
                        file.size > 512 * 1024 ||
                        file.size === 0
                      ) {
                        setError('Choisissez un PNG, JPEG ou WebP de 512 Ko maximum.');
                        return;
                      }
                      void perform(() => hostingProviderApi.saveLogo(current, file, csrfToken));
                    }}
                  />
                </label>
                <small>
                  PNG, JPEG ou WebP · 512 Ko maximum. Les logos clairs reçoivent un fond noir
                  arrondi.
                </small>
                {current.hasLogo && (
                  <button
                    type="button"
                    onClick={() => {
                      void perform(() => hostingProviderApi.removeLogo(current, csrfToken));
                    }}
                  >
                    Retirer le logo
                  </button>
                )}
              </section>
              <section
                className="hosting-providers-editor__source"
                aria-label="Provenance de l’hébergeur"
              >
                <h3>Provenance</h3>
                <p>{origin(current)}</p>
                {current.sourceReferenceId ? (
                  <p>Fournisseur Freebe : {current.sourceDisplayName ?? 'Source liée'}</p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setAttach((value) => !value);
                        setSource(null);
                      }}
                    >
                      {attach ? 'Annuler le rattachement' : 'Rattacher un fournisseur Freebe'}
                    </button>
                    {attach && (
                      <>
                        <SupplierPicker selected={source} onSelect={setSource} />
                        <p>Le rattachement conserve le nom et le logo locaux.</p>
                        <button
                          type="button"
                          disabled={!source}
                          onClick={() => {
                            if (source)
                              void perform(() =>
                                hostingProviderApi.attach(current, source.id, csrfToken),
                              );
                          }}
                        >
                          Rattacher le fournisseur choisi
                        </button>
                      </>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </fieldset>
        <footer>
          <button type="button" onClick={onClose} disabled={busy}>
            Fermer
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
function SupplierPicker({
  selected,
  onSelect,
}: {
  readonly selected: HostingSupplierCandidate | null;
  readonly onSelect: (value: HostingSupplierCandidate) => void;
}): React.JSX.Element {
  const [q, setQ] = useState(''),
    [page, setPage] = useState(0),
    [visit, setVisit] = useState(0),
    [data, setData] = useState<HostingSupplierPage | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void hostingProviderApi
        .sources(q, page)
        .then((result) => {
          if (active) setData(result);
        })
        .catch((reason) => {
          if (active) {
            setData(null);
            setError(hostingProviderError(reason));
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [q, page, visit]);
  return (
    <div className="hosting-providers-source-picker">
      <label>
        Rechercher un fournisseur Freebe
        <input
          value={q}
          maxLength={120}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(0);
          }}
        />
      </label>
      {selected && <p role="status">Fournisseur choisi : {selected.displayName}</p>}
      {loading ? (
        <p role="status">Recherche des fournisseurs…</p>
      ) : error ? (
        <p className="tosho-feedback is-error" role="alert">
          {error}
          <button type="button" onClick={() => setVisit((n) => n + 1)}>
            Réessayer
          </button>
        </p>
      ) : (
        <>
          <ul aria-label="Fournisseurs Freebe disponibles">
            {data?.items.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  aria-pressed={selected?.id === candidate.id}
                  disabled={candidate.hostingProviderId !== null}
                  onClick={() => onSelect(candidate)}
                >
                  {candidate.displayName}
                  {candidate.hostingProviderId !== null ? ' · Déjà au catalogue' : ''}
                </button>
              </li>
            ))}
          </ul>
          {data?.items.length === 0 && (
            <p>Aucun fournisseur dans ces résultats. La saisie manuelle reste disponible.</p>
          )}
          <div className="hosting-provider-picker__pages">
            <button type="button" disabled={page === 0} onClick={() => setPage((n) => n - 1)}>
              Fournisseurs précédents
            </button>
            <button
              type="button"
              disabled={!data || (page + 1) * data.pageSize >= data.total}
              onClick={() => setPage((n) => n + 1)}
            >
              Fournisseurs suivants
            </button>
          </div>
        </>
      )}
    </div>
  );
}
